import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from '@/components/ui'
import { Loader2, Eye, ArrowLeft, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react'
import { jobsApi } from '@/services/api'

interface JobData {
  id: string
  type: string
  customerId: string
  status: string
  progress: number
  inputFileUrls?: string[]
  error?: string
  result?: {
    risk_score?: number
    memo_id?: string
    [key: string]: unknown
  }
  createdAt: string
  completedAt?: string
}

type AnalysisStatus = 'loading' | 'idle' | 'running' | 'completed' | 'failed'

export function Analysis() {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<AnalysisStatus>(jobId ? 'loading' : 'idle')
  const [job, setJob] = useState<JobData | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Fetch job data if jobId is provided
  useEffect(() => {
    if (jobId) {
      const fetchJob = async () => {
        try {
          const jobData = await jobsApi.get(jobId)
          setJob(jobData as unknown as JobData)
          
          if (jobData.status === 'completed') {
            setStatus('completed')
            // Auto-navigate to memo if available
            const memoId = (jobData as unknown as JobData).result?.memo_id
            if (memoId) {
              const jobType = (jobData as unknown as JobData).type
              if (jobType === 'amazon_growth') {
                navigate(`/growth-memo/${memoId}`)
              } else {
                navigate(`/memo/${memoId}`)
              }
            }
          } else if (jobData.status === 'failed') {
            setStatus('failed')
            setError((jobData as unknown as JobData).error || 'Analysis failed')
          } else if (jobData.status === 'pending' || jobData.status === 'processing' || jobData.status === 'running') {
            setStatus('running')
            // Poll for updates
            const pollInterval = setInterval(async () => {
              try {
                const updatedJob = await jobsApi.get(jobId)
                setJob(updatedJob as unknown as JobData)
                
                if (updatedJob.status === 'completed') {
                  clearInterval(pollInterval)
                  setStatus('completed')
                  const memoId = (updatedJob as unknown as JobData).result?.memo_id
                  if (memoId) {
                    const jobType = (updatedJob as unknown as JobData).type
                    if (jobType === 'amazon_growth') {
                      navigate(`/growth-memo/${memoId}`)
                    } else {
                      navigate(`/memo/${memoId}`)
                    }
                  }
                } else if (updatedJob.status === 'failed') {
                  clearInterval(pollInterval)
                  setStatus('failed')
                  setError((updatedJob as unknown as JobData).error || 'Analysis failed')
                }
              } catch (err) {
                console.error('Polling error:', err)
              }
            }, 2000)
            
            // Cleanup on unmount
            return () => clearInterval(pollInterval)
          }
        } catch (err) {
          console.error('Failed to fetch job:', err)
          setStatus('failed')
          setError('Failed to fetch job details')
        }
      }
      fetchJob()
    }
  }, [jobId, navigate])

  // Determine risk category from score (10-point scale: 1-10, higher = riskier)
  const getRiskCategory = (score: number) => {
    if (score <= 3) return 'Low Risk'
    if (score <= 5) return 'Medium Risk'
    if (score <= 7) return 'High Risk'
    return 'Critical Risk'
  }

  const inputFileUrls = job?.inputFileUrls || []

  // Loading state
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-dashboard-teal" />
      </div>
    )
  }

  const riskScore = job?.result?.risk_score
  const memoId = job?.result?.memo_id
  const customerName = job?.customerId || 'Unknown'
  const jobType = job?.type?.toUpperCase() || 'ANALYSIS'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/reports">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {job ? `Analysis: ${customerName}` : 'Credit Analysis'}
              </h1>
              {job && (
                <Badge variant="outline" className="uppercase">
                  {jobType}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              {status === 'completed' 
                ? `Completed on ${job?.completedAt ? new Date(job.completedAt).toLocaleDateString() : 'N/A'}`
                : status === 'failed'
                ? 'Analysis failed'
                : 'Processing analysis...'
              }
            </p>
          </div>
        </div>
        {status === 'completed' && memoId && (
          <Button className="gap-2" onClick={() => navigate(`/memo/${memoId}`)}>
            <Eye className="h-4 w-4" />
            View Memo
          </Button>
        )}
      </div>

      {inputFileUrls.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Input File URLs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {inputFileUrls.map((u) => (
                <div key={u} className="truncate">
                  <a
                    href={u}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-dashboard-teal underline"
                  >
                    {u}
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Idle state - no job */}
      {status === 'idle' && !jobId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">No Analysis Selected</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Select an analysis from the Reports page or start a new analysis from Upload.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/reports')}>
                View Reports
              </Button>
              <Button onClick={() => navigate('/upload')}>
                New Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Running state */}
      {status === 'running' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Analysis in Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="h-10 w-10 rounded-full border-4 border-dashboard-teal/20"></div>
                  <div className="absolute top-0 left-0 h-10 w-10 rounded-full border-4 border-transparent border-t-dashboard-teal animate-spin"></div>
                </div>
                <div>
                  <span className="font-medium">Processing analysis for {customerName}...</span>
                  <p className="text-sm text-muted-foreground">
                    Agents are analyzing customer data. This may take a moment.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Generating credit risk assessment...</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failed state */}
      {status === 'failed' && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Analysis Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600 mb-4">{error || 'An unknown error occurred'}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/reports')}>
                Back to Reports
              </Button>
              <Button variant="outline" onClick={() => navigate('/upload')} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed state */}
      {status === 'completed' && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-6">
            <div className="flex flex-col items-center justify-center gap-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
              <div className="text-center">
                <p className="text-lg font-medium text-green-800">Analysis Complete</p>
                <p className="text-sm text-green-600">
                  Credit memo generated for {customerName}
                </p>
                {riskScore !== undefined && (
                  <p className="text-sm text-green-600 mt-1">
                    Risk Score: {Math.round(riskScore)}/10 • {getRiskCategory(riskScore)}
                  </p>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                {memoId ? (
                  <Button className="gap-2" onClick={() => navigate(`/memo/${memoId}`)}>
                    <Eye className="h-4 w-4" />
                    View Memo
                  </Button>
                ) : (
                  <p className="text-sm text-amber-600">No memo was generated</p>
                )}
                <Button variant="outline" onClick={() => navigate('/reports')}>
                  Back to Reports
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
